<script setup lang="ts">
import type { FlatStudy } from '../store/dicom-web/current-study-store';
import type { OrthancSeriesInfo } from '@/src/core/orthanc-api';

import { useAppSettings } from '@src/composables/useAppSettings';
import { useAppState } from '@src/store/ocg-app-state';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';

import LoadSaveAnalysisStateDialog from '@/src/components/LoadSaveAnalysisStateDialog.vue';
import { useGeneralPurposeOverlayManager } from '@/src/store/overlay-management/overlay-manager';

import { useActiveStudyStore } from '../store/active-study';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import { useOrthancStore } from '../store/dicom-web/orthanc-store';

import SquareLoader from './SquareLoader.vue';

const props = defineProps({
  study: {
    type: Object,
    required: true,
  },
});

function flattenSeries(s: OrthancSeriesInfo) {
  return {
    id: s.ID,
    uid: s.MainDicomTags.SeriesInstanceUID,
    // eslint-disable-next-line dot-notation
    description: s.MainDicomTags['SeriesDescription'] || '',
    // eslint-disable-next-line dot-notation
    modality: s.MainDicomTags['Modality'] || '',
    // eslint-disable-next-line dot-notation
    seriesNumber: Number.parseInt(s.MainDicomTags['SeriesNumber'] || '0', 10),
    instanceCount: s.Instances.length,
    firstInstanceId: s.Instances[0] || '',
  };
}

const orthancStore = useOrthancStore();
const activeStudyStore = useActiveStudyStore();
const currentStudyStore = useCurrentStudyStore();
const router = useRouter();

const isLoading = ref(false);

const studySeriesInfo = computed(() => orthancStore.studySeries);
const seriesList = computed(() =>
  studySeriesInfo.value
    .map(flattenSeries)
    .sort((a, b) => a.seriesNumber - b.seriesNumber),
);

const thumbnails = reactive<Record<string, string>>({});

watch(
  () => orthancStore.studySeries,
  async (raw) => {
    const list = raw
      .map(flattenSeries)
      .sort((a, b) => a.seriesNumber - b.seriesNumber);
    if (!list.length) return;
    const BATCH_SIZE = 3;

    for (let i = 0; i < list.length; i += BATCH_SIZE) {
      const batch = list.slice(i, i + BATCH_SIZE);
      const results = await Promise.all(
        batch
          .filter(s => s.firstInstanceId && !thumbnails[s.id])
          .map(s =>
            orthancStore
              .fetchThumbnail(s.firstInstanceId)
              .then(url => ({ id: s.id, url })),
          ),
      );
      results.forEach(({ id, url }) => {
        if (url) thumbnails[id] = url;
      });
      await new Promise((r) => {
        setTimeout(r, 0);
      });
    }
  },
);

const loadSaveAnalysisStateDialog = useTemplateRef<
  InstanceType<typeof LoadSaveAnalysisStateDialog>
>('loadSaveAnalysisStateDialog');

onMounted(async () => {
  try {
    if (!props.study?.OrthancID) {
      console.error('Study missing OrthancID');
      return;
    }
    await orthancStore.fetchStudySeries(props.study.OrthancID);
    orthancStore.startSeriesPolling(10000);
  }
  catch (e) {
    console.error('Failed to load series/thumbnails:', e);
  }
});

onBeforeUnmount(() => {
  orthancStore.stopSeriesPolling();
});

const isStableStudy = computed(() => {
  const uid = props.study?.StudyInstanceUID;
  if (!uid) return false;
  const live = orthancStore.studies.find(
    s => s.MainDicomTags.StudyInstanceUID === uid,
  );
  return live?.IsStable ?? false;
});

const initCurrentStudy = async () => {
  try {
    if (!isStableStudy.value) {
      return;
    }
    isLoading.value = true;
    await currentStudyStore.initStudy(
      props.study as FlatStudy,
      studySeriesInfo.value,
    );
    activeStudyStore.initStudyData();

    const numSeries = currentStudyStore.getSerieCount();
    for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex++) {
      const sliceCount = currentStudyStore.getSliceCount(seriesIndex);
      for (let sliceIndex = 0; sliceIndex < sliceCount; sliceIndex++) {
        const numImages = currentStudyStore.getImagesCount(
          seriesIndex,
          sliceIndex,
        );
        activeStudyStore.addStudyData(
          seriesIndex.toString(),
          seriesIndex,
          sliceIndex,
          numImages,
        );
      }
    }

    activeStudyStore.setActive(props.study.StudyInstanceUID);
    activeStudyStore.setPatientInfo(props.study.StudyInstanceUID);

    const studyID = currentStudyStore.getStudyID;
    loadSaveAnalysisStateDialog.value?.loadLatestSave(studyID);
    const { applyAll } = useAppSettings();
    applyAll();
    useAppState().setView('QualityCheck');
    const overlayManager = useGeneralPurposeOverlayManager();
    overlayManager.showGeneralPurposeOverlay({ viewerType: 'viewer' });

    await router.push('/analysis');
  }
  catch (error) {
    console.error('Failed to initialize current study:', error);
  }
  finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col overflow-hidden h-full min-h-0"
    :class="{ 'study-not-stable': !isStableStudy }"
  >
    <div class="flex items-center gap-4 mb-4">
      <div>
        <button
          class="button btn-primary"
          :disabled="isLoading || !isStableStudy"
          @click="initCurrentStudy"
        >
          <p>Load Study</p>
        </button>
      </div>
    </div>
    <div class="thumbnail-viewer-container flex-grow min-h-0 overflow-y-auto">
      <div
        v-if="isLoading"
        class="mt-10 mx-auto flex flex-col items-center cursor-progress"
      >
        <v-progress-circular indeterminate />
        Loading Study...
      </div>
      <div v-else class="flex flex-wrap gap-2">
        <div
          v-for="series in seriesList"
          :key="series.id"
          class="small-thumbnail-image-container"
        >
          <div class="small-thumbnail-image">
            <img
              v-if="thumbnails[series.id]"
              :src="thumbnails[series.id]"
              class="w-full h-full object-cover"
              :alt="series.description"
            >
            <SquareLoader v-else />
            <div class="overlay-thumbnail-tag">
              <p>{{ series.instanceCount }}</p>
              <i class="fa-solid fa-images fa-sm" />
            </div>
          </div>
          <div class="break-words" style="width: var(--small-thumbnail-width)">
            <p>{{ series.description || '(no series description)' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <LoadSaveAnalysisStateDialog ref="loadSaveAnalysisStateDialog" />
</template>

<style scoped>
.thumbnail-viewer-container::-webkit-scrollbar {
  display: none;
  width: 0 !important;
}

.active {
  background: var(--md-primary);
}

.thumbnail-skeleton {
  width: var(--small-thumbnail-width, 120px);
  height: var(--small-thumbnail-width, 120px);
  background: var(--md-bg);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
}

.study-not-stable {
  opacity: 0.45;
  pointer-events: none;
  position: relative;
}
</style>
